import React, { useState, useEffect } from 'react';
import { Phone, Search, User, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status color mapping for visual distinction
const getStatusColor = (status) => {
  const colors = {
    'New Lead': 'bg-blue-100 text-blue-800 border-blue-200',
    'Uncontacted': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Qualified Lead': 'bg-green-100 text-green-800 border-green-200',
    'Virtual Meet': 'bg-purple-100 text-purple-800 border-purple-200',
    'Awaited Visit': 'bg-orange-100 text-orange-800 border-orange-200',
    'Awaited LOI': 'bg-pink-100 text-pink-800 border-pink-200',
    'Closed LOI': 'bg-emerald-100 text-emerald-800 border-emerald-200',
    'Outlet Opened': 'bg-teal-100 text-teal-800 border-teal-200',
    'Nurture': 'bg-indigo-100 text-indigo-800 border-indigo-200',
    'Dead': 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return colors[status] || 'bg-slate-100 text-slate-800 border-slate-200';
};

const QuickCallPage = () => {
  const [phonebook, setPhonebook] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [totalLeads, setTotalLeads] = useState(0);

  useEffect(() => {
    fetchPhonebook();
  }, []);

  const fetchPhonebook = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const response = await fetch(`${API_URL}/api/leads/phonebook`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch phonebook');
      }

      const data = await response.json();
      setPhonebook(data.phonebook || {});
      
      // Count total leads
      let total = 0;
      Object.values(data.phonebook || {}).forEach(leads => {
        total += leads.length;
      });
      setTotalLeads(total);
      
      // Expand all sections by default
      const expanded = {};
      Object.keys(data.phonebook || {}).forEach(status => {
        expanded[status] = true;
      });
      setExpandedSections(expanded);
      
    } catch (error) {
      console.error('Error fetching phonebook:', error);
      toast.error('Failed to load phonebook');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (status) => {
    setExpandedSections(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  const handleCall = (mobile, name) => {
    if (!mobile) {
      toast.error('No phone number available');
      return;
    }
    
    // Clean the phone number
    const cleanNumber = mobile.replace(/[^0-9+]/g, '');
    
    // Open device dialer
    window.location.href = `tel:${cleanNumber}`;
    
    toast.success(`Calling ${name}...`);
  };

  // Filter leads based on search term
  const getFilteredPhonebook = () => {
    if (!searchTerm.trim()) return phonebook;
    
    const filtered = {};
    const term = searchTerm.toLowerCase();
    
    Object.entries(phonebook).forEach(([status, leads]) => {
      const matchedLeads = leads.filter(lead => 
        lead.name?.toLowerCase().includes(term) ||
        lead.mobile?.includes(term)
      );
      if (matchedLeads.length > 0) {
        filtered[status] = matchedLeads;
      }
    });
    
    return filtered;
  };

  const filteredPhonebook = getFilteredPhonebook();
  const statusList = Object.keys(filteredPhonebook).sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="quick-call-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Phone className="h-6 w-6 text-red-600" />
          Quick Call
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {totalLeads} leads in phonebook
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="phonebook-search"
          />
        </div>
      </div>

      {/* Phonebook Sections */}
      {statusList.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Phone className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No leads match your search' : 'No leads assigned to you'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {statusList.map(status => {
            const leads = filteredPhonebook[status] || [];
            const isExpanded = expandedSections[status];
            
            return (
              <Card key={status} className="overflow-hidden" data-testid={`status-section-${status}`}>
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(status)}
                  className={`w-full px-4 py-3 flex items-center justify-between ${getStatusColor(status)} border-b transition-colors hover:opacity-90`}
                  data-testid={`toggle-${status}`}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-semibold">{status}</span>
                    <Badge variant="secondary" className="bg-white/50">
                      {leads.length}
                    </Badge>
                  </div>
                </button>

                {/* Leads List */}
                {isExpanded && (
                  <div className="divide-y divide-gray-100">
                    {leads.map((lead, index) => (
                      <div
                        key={lead.id || index}
                        className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        data-testid={`lead-row-${lead.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {lead.name || 'Unknown'}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {lead.mobile || 'No phone'}
                            </p>
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          className="flex-shrink-0 h-10 w-10 p-0 rounded-full bg-green-50 hover:bg-green-100 text-green-600"
                          onClick={() => handleCall(lead.mobile, lead.name)}
                          disabled={!lead.mobile}
                          data-testid={`call-btn-${lead.id}`}
                        >
                          <Phone className="h-5 w-5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Quick Stats */}
      <div className="mt-6 flex flex-wrap gap-2">
        {statusList.map(status => (
          <Badge 
            key={status} 
            variant="outline" 
            className={`${getStatusColor(status)} cursor-pointer`}
            onClick={() => {
              setExpandedSections(prev => {
                const allClosed = {};
                statusList.forEach(s => allClosed[s] = s === status);
                return allClosed;
              });
            }}
          >
            {status}: {filteredPhonebook[status]?.length || 0}
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default QuickCallPage;
